import styled, { createGlobalStyle } from "styled-components";
import { token, radius, space, font, ease } from "../theme";

export const GlobalStyle = createGlobalStyle`
  * {
    box-sizing: border-box;
  }

  /*
   * The document is the only scroll container. Pinning body to height:100% with
   * an overflow made body a second scroller, which some WebViews handle badly
   * once the result card pushes the page past one viewport.
   */
  html {
    min-height: 100%;
    overflow-x: hidden;
    color-scheme: dark;
  }

  body {
    min-height: 100%;
    margin: 0;
    padding: 0;
    background-color: ${token.bgPage};
    color: ${token.textPrimary};
    font-family: ${font.sans};
    font-size: 1rem;
    line-height: 1.5;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    /* Keep the pull-to-refresh gesture of in-app browsers off the viewfinder. */
    overscroll-behavior-y: none;
  }

  :focus-visible {
    outline: 2px solid ${token.accent};
    outline-offset: 2px;
  }
`;

export const Root = styled.main`
  display: flex;
  flex-flow: column;
  align-items: center;
  gap: ${space.lg};
  min-height: 100vh;
  padding: ${space.lg} ${space.md} ${space.xl};
  text-align: center;
  overflow-x: hidden;

  /* env() keeps the layout clear of the notch when the WebView is edge to edge. */
  padding-top: calc(${space.lg} + env(safe-area-inset-top, 0px));
  padding-bottom: calc(${space.xl} + env(safe-area-inset-bottom, 0px));
`;

export const Header = styled.header`
  display: flex;
  flex-flow: column;
  align-items: center;
  gap: ${space.sm};
`;

export const Brand = styled.img`
  display: block;
  width: 168px;
  height: auto;
`;

/**
 * The eyebrow bar: 3px vertical teal rule plus an uppercase tracked label.
 * Signature motif of the design system, present on every section header.
 */
export const Eyebrow = styled.p`
  display: inline-flex;
  align-items: center;
  gap: ${space.sm};
  margin: 0;
  color: ${token.accent};
  font-size: 0.8125rem;
  font-weight: 700;
  line-height: 1.2;
  letter-spacing: 0.15em;
  text-transform: uppercase;

  &::before {
    content: "";
    width: 3px;
    height: 1.25em;
    background-color: ${token.accent};
  }
`;

export const Stage = styled.div`
  display: flex;
  flex-flow: column;
  align-items: center;
  gap: ${space.md};
  width: 100%;
  max-width: 320px;
`;

export const Hint = styled.p`
  margin: 0;
  max-width: 320px;
  color: ${token.textSecondary};
  font-size: 0.875rem;
`;

export const Actions = styled.footer`
  display: flex;
  flex-flow: column;
  gap: ${space.sm};
  width: 100%;
  max-width: 320px;
  /* Anchors the actions to the bottom of tall viewports. */
  margin-top: auto;
`;

/**
 * Primary action. Nearly square corners, mechanical transition, navy text on
 * teal. Never a pill.
 */
export const PrimaryButton = styled.button`
  appearance: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  padding: ${space.md} ${space.lg};
  border: 1px solid ${token.accent};
  border-radius: ${radius.sm};
  background-color: ${token.accent};
  color: ${token.textOnAccent};
  font-family: inherit;
  font-size: 1rem;
  font-weight: 600;
  line-height: 1.2;
  text-align: center;
  text-decoration: none;
  cursor: pointer;
  transition: background-color 150ms ${ease}, border-color 150ms ${ease};

  &:hover {
    background-color: ${token.accentHover};
    border-color: ${token.accentHover};
  }

  &:disabled {
    opacity: 0.5;
    cursor: default;
  }
`;

export const SecondaryButton = styled(PrimaryButton)`
  border-color: ${token.borderStrong};
  background-color: transparent;
  color: ${token.textPrimary};

  &:hover {
    border-color: ${token.textPrimary};
    background-color: transparent;
  }
`;

export const Card = styled.div`
  width: 100%;
  padding: ${space.lg};
  border: 1px solid ${token.borderDefault};
  border-radius: ${radius.md};
  background-color: ${token.bgSurface};
  text-align: left;
`;

/** Card with the 4px accent top border, used for the decoded payload. */
export const ResultCard = styled(Card)`
  border-top: 4px solid ${token.accent};
  border-radius: ${radius.sm} ${radius.sm} ${radius.md} ${radius.md};
`;

export const ResultLabel = styled.p`
  margin: 0 0 ${space.xs};
  color: ${token.textMuted};
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.15em;
  text-transform: uppercase;
`;

export const ResultValue = styled.p`
  margin: 0;
  color: ${token.textPrimary};
  font-family: ${font.mono};
  font-size: 0.8125rem;
  line-height: 1.5;
  white-space: pre-wrap;
  word-break: break-all;
`;

/** Advisory panel. Amber marks a warning and is never decorative. */
export const Notice = styled(Card)`
  border-left: 4px solid ${token.warning};
  border-radius: ${radius.sm} ${radius.md} ${radius.md} ${radius.sm};
  color: ${token.textSecondary};
  font-size: 0.875rem;
`;
